import { useState } from 'react';
import {
  Badge,
  BlockStack,
  Button,
  Card,
  DataTable,
  InlineStack,
  ProgressBar,
  Spinner,
  Text,
} from '@shopify/polaris';
import { useDeleteReport, useReport, useReports } from '../../hooks/useReports';
import type { StoreReport } from '../../api/reports';

function statusTone(status: StoreReport['status']): 'success' | 'attention' | 'critical' | 'info' {
  switch (status) {
    case 'complete': return 'success';
    case 'pending':  return 'attention';
    case 'failed':   return 'critical';
    default:         return 'info';
  }
}

function scoreTone(score: number): 'success' | 'critical' {
  return score >= 50 ? 'success' : 'critical';
}

// ── Report Detail ──────────────────────────────────────────────────────────

function ReportDetail({ id, onBack }: { id: number; onBack: () => void }) {
  const { data, isLoading } = useReport(id);
  const report = data?.data;

  if (isLoading) {
    return (
      <div style={{ display: 'flex', justifyContent: 'center', padding: '32px' }}>
        <Spinner size="small" />
      </div>
    );
  }

  if (!report) return null;

  const typeRows = Object.entries(report.checks_by_type).map(([type, counts]) => [
    type.charAt(0).toUpperCase() + type.slice(1),
    `${counts.passed}/${counts.total}`,
    `${Math.round((counts.passed / Math.max(counts.total, 1)) * 100)}%`,
    counts.total - counts.passed > 0
      ? <Badge tone="critical">{`${counts.total - counts.passed} issues`}</Badge>
      : <Badge tone="success">All passed</Badge>,
  ]);

  return (
    <BlockStack gap="500">
      <InlineStack gap="200" blockAlign="center" align="space-between">
        <Button variant="plain" onClick={onBack}>← Back to reports</Button>
        <Badge tone={statusTone(report.status)}>{report.status_label}</Badge>
      </InlineStack>

      <Text as="h2" variant="headingLg">{report.title}</Text>

      {/* Score card */}
      <Card>
        <BlockStack gap="300">
          <InlineStack gap="300" blockAlign="center">
            <Text as="h3" variant="headingMd">Overall Score</Text>
            <Badge
              tone={scoreTone(report.overall_score)}
              size="large"
            >
              {`${report.overall_score}/100`}
            </Badge>
          </InlineStack>
          <ProgressBar progress={report.overall_score} tone={scoreTone(report.overall_score)} />
          <Text as="p" variant="bodySm" tone="subdued">
            {report.passed_checks} of {report.total_checks} checks passed
            ({Math.round(report.pass_rate * 100)}% pass rate)
          </Text>
        </BlockStack>
      </Card>

      {/* AI summary */}
      {report.is_ready && report.ai_summary && (
        <Card>
          <BlockStack gap="200">
            <Text as="h3" variant="headingMd">AI Executive Summary</Text>
            <Text as="p" variant="bodyMd">{report.ai_summary}</Text>
            {report.cost_usd > 0 && (
              <Text as="p" variant="bodySm" tone="subdued">
                Generated using {report.ai_model} · ${report.cost_usd.toFixed(5)} ·{' '}
                {(report.prompt_tokens ?? 0) + (report.completion_tokens ?? 0)} tokens
              </Text>
            )}
          </BlockStack>
        </Card>
      )}

      {/* Breakdown by type */}
      {typeRows.length > 0 && (
        <Card>
          <BlockStack gap="300">
            <Text as="h3" variant="headingMd">Checks by Category</Text>
            <DataTable
              columnContentTypes={['text', 'text', 'text', 'text']}
              headings={['Category', 'Passed', 'Pass rate', 'Status']}
              rows={typeRows}
            />
          </BlockStack>
        </Card>
      )}

      {/* Recommendations */}
      <Card>
        <BlockStack gap="200">
          <Text as="h3" variant="headingMd">AI Recommendations</Text>
          <InlineStack gap="300">
            {Object.entries(report.recommendation_counts).map(([status, count]) => (
              <Text key={status} as="span" variant="bodySm">
                <strong>{count}</strong> {status}
              </Text>
            ))}
          </InlineStack>
        </BlockStack>
      </Card>
    </BlockStack>
  );
}

// ── Reports list ───────────────────────────────────────────────────────────

interface StoreReportsPanelProps {
  activeId: number | null;
  onSelectReport: (id: number | null) => void;
  hasGenerateError?: boolean;
}

export function StoreReportsPanel({ activeId, onSelectReport, hasGenerateError }: StoreReportsPanelProps) {
  const [page, setPage] = useState(1);

  const { data, isLoading } = useReports(page);
  const { mutate: deleteReport } = useDeleteReport();

  const reports = data?.data ?? [];
  const meta    = data?.meta;

  if (activeId !== null) {
    return <ReportDetail id={activeId} onBack={() => onSelectReport(null)} />;
  }

  return (
    <BlockStack gap="500">
      {hasGenerateError && (
        <Text as="p" variant="bodySm" tone="critical">
          Could not generate report — make sure you have a completed audit run first.
        </Text>
      )}

      {isLoading ? (
        <div style={{ display: 'flex', justifyContent: 'center', padding: '32px' }}>
          <Spinner size="small" />
        </div>
      ) : reports.length === 0 ? (
        <Card>
          <Text as="p" variant="bodySm" tone="subdued">
            No reports yet. Run a store audit first, then generate your first report.
          </Text>
        </Card>
      ) : (
        <BlockStack gap="300">
          {reports.map((report) => (
            <Card key={report.id}>
              <InlineStack gap="200" blockAlign="center" align="space-between">
                <BlockStack gap="100">
                  <Button variant="plain" textAlign="left" onClick={() => onSelectReport(report.id)}>
                    {report.title}
                  </Button>
                  <Text as="p" variant="bodySm" tone="subdued">
                    Score: <strong>{report.overall_score}/100</strong> ·{' '}
                    {report.passed_checks}/{report.total_checks} checks passed ·{' '}
                    {report.created_at ? new Date(report.created_at).toLocaleDateString() : ''}
                  </Text>
                </BlockStack>
                <InlineStack gap="150">
                  <Badge tone={statusTone(report.status)}>{report.status_label}</Badge>
                  <Button
                    size="slim"
                    variant="plain"
                    tone="critical"
                    onClick={() => deleteReport(report.id)}
                  >
                    Delete
                  </Button>
                </InlineStack>
              </InlineStack>
            </Card>
          ))}
        </BlockStack>
      )}

      {meta && meta.last_page > 1 && (
        <InlineStack gap="200" align="center">
          <Button size="slim" disabled={page <= 1} onClick={() => setPage((p) => p - 1)}>
            Previous
          </Button>
          <Text as="span" variant="bodySm">Page {meta.current_page} of {meta.last_page}</Text>
          <Button size="slim" disabled={page >= meta.last_page} onClick={() => setPage((p) => p + 1)}>
            Next
          </Button>
        </InlineStack>
      )}
    </BlockStack>
  );
}
